import { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { pick } from 'lodash';
import { price } from '@/components/ShipmentForm/utilities/processOrders';
import { buildServiceLineFromCatalog } from '@/components/ShipmentForm/utilities/serviceCatalog';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';
import { ORDER_BY_ID, LINKED_ORDERS } from '../queries';
import {
    DELETE_SERVICE,
    INSERT_SERVICE,
    ORDER_NAMES_BY_ID,
    SERVICE_CLONES_BY_ORIGINAL,
    SPLIT_SERVICE_ORDER,
    UPDATE_SERVICE,
} from './graphql';
import { useRecalcStopServiceTimes } from '@/hooks';
import { PRICED_RATE_FIELDS } from '@/components/ShipmentForm/utilities/pricingFields';
import { buildServiceClone } from './serviceClone';

// Thrown by splitServices after it has already notified the user, so callers can
// skip double-toasting this specific failure.
export const SERVICE_SPLIT_PRICING_FAILED = 'SERVICE_SPLIT_PRICING_FAILED';

// Thrown when a service mutation failed. `onError` has already toasted and captured it, so
// callers use this only as a control-flow signal — don't toast it again.
export const SERVICE_MUTATION_FAILED = 'SERVICE_MUTATION_FAILED';

// Supplying `onError` makes useMutation swallow the rejection and resolve with
// { data: undefined, errors } instead, so an unchecked `await` walks straight into the
// downstream reprice/recalc. `errors` is an ApolloError object on that path (no `.length`)
// and a GraphQLError[] when the response itself carried errors — test truthiness, not length.
const runMutation = async (mutate, variables) => {
    const result = await mutate({ variables });
    if (result?.errors) throw new Error(SERVICE_MUTATION_FAILED);
    return result;
};

// Service-line CRUD + "split onto a new service order" for the order details Services tab. All
// mutations refetch the order (so the grid reflects adds/edits/removes) and the linked-orders query
// (so a new clone appears in the breadcrumb). `onNotify` surfaces failures to the user (the caller
// passes the order page's setNotification); without it errors are still logged to Sentry.
export const useOrderServices = (order, { onNotify } = {}) => {
    const client = useApolloClient();
    const rootId = order?.original_order_id || order?.order_id;

    const notify = (severity, message) => onNotify?.({ severity, message });

    const opts = {
        refetchQueries: [
            { query: ORDER_BY_ID, variables: { order_id: order?.order_id } },
            { query: LINKED_ORDERS, variables: { root_id: rootId } },
        ],
        awaitRefetchQueries: true,
        onError: (e) => {
            console.error(e);
            captureException(e);
            notify('error', 'Something went wrong updating services. Please try again.');
        },
    };

    const [insertService, { loading: adding }] = useMutation(INSERT_SERVICE, opts);
    const [updateService] = useMutation(UPDATE_SERVICE, opts);
    const [deleteService] = useMutation(DELETE_SERVICE, opts);
    const [splitServiceOrder, { loading: splitting }] = useMutation(SPLIT_SERVICE_ORDER, opts);
    // Deliberately no onError: it would swallow the rejection and make a failed rate-field write
    // look like a successful reprice. Let it reject into repriceOrder's catch, which logs,
    // captures, and warns that the price needs review.
    const [updateOrder] = useMutation(UPDATE_ORDER_BY_ID);

    const recalcStopServiceTimes = useRecalcStopServiceTimes(order);

    // The service edit is already committed by the time this runs, so a failure leaves the stop's
    // service_time stale and the route unflagged — warn instead of failing silently.
    const flagStopsForRecalc = useCallback(async () => {
        const ok = await recalcStopServiceTimes();
        if (!ok) {
            notify(
                'warning',
                'Services were updated, but the route stop times could not be refreshed. Please re-optimize the route.'
            );
        }
        return ok;
    }, [recalcStopServiceTimes, onNotify]);

    const services = order?.services || [];

    // Service-line CRUD doesn't reprice on its own, so changing billable services would leave a stale
    // breakdown. After a mutation's refetch settles, re-run pricing on the fresh order (now carrying
    // the updated service lines) and persist the resulting rate fields. Pricing failures are left
    // non-fatal so the service edit still stands; the existing breakdown is kept untouched.
    const repriceOrder = useCallback(async () => {
        try {
            const { data } = await client.query({
                query: ORDER_BY_ID,
                variables: { order_id: order.order_id },
                fetchPolicy: 'cache-first',
            });
            const fresh = data?.orders?.[0];
            if (!fresh) return;

            const [failed, success = []] = await price([{ ...fresh, key: fresh.order_id }]);
            const priced = success[0];
            if (!priced || failed[fresh.order_id] || !priced.price_breakdown) {
                notify(
                    'warning',
                    'Services were updated, but the order price could not be refreshed. Please review the order pricing.'
                );
                return;
            }

            await updateOrder({
                variables: { order_id: fresh.order_id, update: pick(priced, PRICED_RATE_FIELDS) },
            });
        } catch (e) {
            console.error(e);
            captureException(e);
            onNotify?.({
                severity: 'warning',
                message:
                    'Services were updated, but the order price could not be refreshed. Please review the order pricing.',
            });
        }
    }, [client, order?.order_id, updateOrder, onNotify]);

    const addService = async (selected, quantity) => {
        await runMutation(insertService, {
            object: { order_id: order.order_id, ...buildServiceLineFromCatalog(selected, quantity) },
        });
        await repriceOrder();
        await flagStopsForRecalc();
    };

    const setBillable = async (service_id, billable) => {
        await runMutation(updateService, { service_id, set: { billable } });
        await repriceOrder();
    };

    // service_time is a dispatch/dwell value, not a pricing input — no reprice.
    const setServiceTime = async (service_id, service_time) => {
        await runMutation(updateService, { service_id, set: { service_time } });
        await flagStopsForRecalc();
    };

    const removeService = async (service_id) => {
        await runMutation(deleteService, { service_id });
        await repriceOrder();
        await flagStopsForRecalc();
    };

    // Split the selected service lines onto a new service order (option b — copy-then-delete).
    // Price the clone client-side (order_type='service' → onwardv2 isService → service-only) so it's
    // inserted with rates, count existing clones for the -S{n} suffix, then insert + delete atomically.
    const splitServices = async (serviceIds, instructions) => {
        const selected = services.filter((s) => serviceIds.includes(s.service_id));
        if (selected.length === 0) return undefined;

        // Resolve the ROOT parent so the clone links to it and is named as a flat sibling, even when splitting a child clone
        let parent = order;
        if (order.original_order_id) {
            const { data: rootData } = await client.query({
                query: ORDER_NAMES_BY_ID,
                variables: { order_id: rootId },
                fetchPolicy: 'cache-first',
            });
            parent = rootData?.orders?.[0] || {
                order_id: rootId,
                order_number: order.order_number,
                po_number: order.po_number,
            };
        }

        const [failed, success = []] = await price([
            { ...order, order_type: 'service', services: selected, key: order.order_id },
        ]);
        const priced = success[0];

        // Abort if the service-only price didn't resolve — otherwise we'd insert a clone with null
        // rates and still delete the service lines from the parent, silently creating a $0 order.
        if (!priced || failed[order.order_id] || !priced.price_breakdown) {
            notify(
                'error',
                "Order pricing needs to be adjusted for these services before they can be split. Please update the order's pricing and try again."
            );
            throw new Error(SERVICE_SPLIT_PRICING_FAILED);
        }

        // Next -S{n} comes from the max existing suffix, not the clone count — counts desync when
        // a clone is hard-deleted, and concurrent splits reading the same count would collide.
        const { data } = await client.query({
            query: SERVICE_CLONES_BY_ORIGINAL,
            variables: { original_order_id: rootId },
            fetchPolicy: 'network-only',
        });
        const clones = data?.orders || [];
        const suffixes = clones
            .map((o) => Number((o.order_number || '').match(/-S(\d+)$/)?.[1]))
            .filter(Number.isFinite);
        const suffixIndex = (suffixes.length ? Math.max(...suffixes) : clones.length) + 1;

        const clone = buildServiceClone({ order, parent, priced, services: selected, suffixIndex, instructions });
        const result = await runMutation(splitServiceOrder, { order: clone, service_ids: serviceIds });
        // The parent lost the moved service lines, so its breakdown is now stale — reprice it.
        await repriceOrder();
        await flagStopsForRecalc();
        return result;
    };

    return { services, adding, splitting, addService, setBillable, setServiceTime, removeService, splitServices };
};
